import { useEffect, useRef } from "react";

/**
 * Debounces handler across multiple window events (e.g. Header SSE fan-out).
 * Uses a ref so the latest handler runs without re-subscribing on every render.
 */
export function useDebouncedWindowEvents(
  eventNames: readonly string[],
  handler: () => void,
  debounceMs: number,
  enabled = true
): void {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;
  const key = eventNames.join("\0");

  useEffect(() => {
    if (!enabled) return;
    let t: ReturnType<typeof setTimeout> | null = null;
    const run = () => {
      if (t) clearTimeout(t);
      t = setTimeout(() => {
        t = null;
        handlerRef.current();
      }, debounceMs);
    };
    for (const ev of eventNames) {
      window.addEventListener(ev, run);
    }
    return () => {
      if (t) clearTimeout(t);
      for (const ev of eventNames) {
        window.removeEventListener(ev, run);
      }
    };
  }, [enabled, debounceMs, key]);
}

const DEFAULT_VISIBLE_FALLBACK_MS = 2 * 60 * 1000;
const DEFAULT_JITTER_MS = 20 * 1000;

/**
 * Rare visible-only safety poll when SSE/events might be missed (tab sleep, reconnect gaps).
 * Self-scheduling with jitter + in-flight guard to avoid overlap.
 */
export function useVisibleStaleFallback(
  callback: () => void | Promise<void>,
  enabled: boolean,
  intervalMs = DEFAULT_VISIBLE_FALLBACK_MS,
  jitterMs = DEFAULT_JITTER_MS
): void {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let inFlight = false;

    const schedule = () => {
      const jitter = Math.floor(Math.random() * jitterMs);
      timeoutId = setTimeout(() => void run(), intervalMs + jitter);
    };

    const run = async () => {
      if (cancelled) return;
      if (typeof document !== "undefined" && document.visibilityState !== "visible") {
        schedule();
        return;
      }
      if (inFlight) {
        schedule();
        return;
      }
      inFlight = true;
      try {
        await Promise.resolve(callbackRef.current());
      } finally {
        inFlight = false;
        schedule();
      }
    };

    schedule();
    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [enabled, intervalMs, jitterMs]);
}
